import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Ray, Vector3 } from 'three';

import { CoordinateManager } from '../../../../../../../viewer/coordinate-manager';
import { PointId } from '../../../../../../../viewer/elements/design/design-node/utils/point-node';
import { PointCloudHandle } from '../../../../../../../viewer/geodata/point-clouds/point-cloud-handle';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { pointHeightFunctions } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/set-point-heights';
import { InterpolationSnapMode } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/point';
import { loadHighResolutionLodPointCloudChunksFromRays } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/point-cloud';
import { deselect } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/select';
import { ViewSettings } from '../../../../../../../viewer/view-settings';
import { useButtonHotkey } from '../../../../../../hooks/use-button-hotkey';
import { Button } from '../../../../../button/button';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import {
  ProgressDialog,
  useProgressDialogDependencies,
} from '../../../../../progress-dialog/progress-dialog';
import { Select } from '../../../../../select/select';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { useCurrentSelection } from '../hooks/use-current-selection';

type Props = {
  tool: DesignTool;
};

const SetPointHeights = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [pointIds, setPointIds] = useState<PointId[]>([]);
  const [snapMode, setSnapMode] = useState<InterpolationSnapMode>('height');
  const [value, setValue] = useState(props.tool.snapValues.height.toFixed(2));
  const confirmButton = useRef<HTMLButtonElement>(null);

  const progress = useProgressDialogDependencies(3);
  const { currentSelection } = useCurrentSelection(props.tool);
  useButtonHotkey(confirmButton, 'Enter');

  const items = useRef([
    {
      id: 'height',
      name: t('section.functions.height.content.mode.item.height', { ns: 'skyviewDesign' }),
    },
    {
      id: 'relativeHeight',
      name: t('section.functions.height.content.mode.item.relativeHeight', { ns: 'skyviewDesign' }),
    },
    ...(ViewSettings.instance.isCadViewer
      ? []
      : [
          {
            id: 'pointCloud',
            name: t('section.functions.height.content.mode.item.pointCloud', {
              ns: 'skyviewDesign',
            }),
          },
        ]),
    {
      id: 'assets',
      name: t('section.functions.height.content.mode.item.object', { ns: 'skyviewDesign' }),
    },
  ]);

  const onSetPointHeights = async () => {
    if (pointIds.length === 0) {
      throw new Error(
        t('section.functions.height.content.save.errorNoPointsSelected', {
          ns: 'skyviewDesign',
        }) as string,
      );
    }

    try {
      const pointCloudResult =
        snapMode === 'pointCloud' && PointCloudHandle.instance.activePointCloud
          ? await loadHighResolutionLodPointCloudChunksFromRays(
              pointIds.map(
                (x) =>
                  new Ray(
                    props.tool.asset.designNode.getVertexByPointId(x, 'world'),
                    new Vector3(0, 0, 1),
                  ),
              ),
              progress.controller.current,
              progress.abortController.current,
              PointCloudHandle.instance.activePointCloud,
            )
          : undefined;

      if (progress.abortController.current.signal.aborted) {
        return;
      }

      const result = await pointHeightFunctions.getSetPointHeightResult(
        props.tool,
        pointIds,
        snapMode,
        Number(value),
        progress.abortController.current,
        progress.controller.current,
        pointCloudResult?.points ?? [],
      );

      for (const child of pointCloudResult?.parent.children ?? []) {
        child.removeFromParent();
      }

      if (progress.abortController.current.signal.aborted) {
        return;
      }

      pointHeightFunctions.applySetPointHeight(props.tool, result);

      deselect(props.tool);

      progress.setShowDialog(false);
    } catch (err) {
      throw new Error(
        t('section.functions.height.content.save.errorUnknownError', {
          ns: 'skyviewDesign',
        }) as string,
      );
    }
  };

  const refresh = React.useCallback(() => {
    setPointIds([...currentSelection.points]);

    if (props.tool.setPointHeightsValueNeedsUpdate) {
      props.tool.setPointHeightsValueNeedsUpdate = false;

      if (props.tool.currentPoint) {
        setValue((props.tool.currentPoint.z + CoordinateManager.getInstance().getMAS()).toFixed(2));
      }
    }
  }, [props.tool, currentSelection]);

  React.useEffect(refresh, [refresh]);

  return (
    <Stack spacing={1}>
      {pointIds.length === 0 ? (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
          leftTooltip={{
            title: t('section.functions.height.content.infoNoPointsSelected.tooltip.title', {
              ns: 'skyviewDesign',
            }),
            content: (
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="section.functions.height.content.infoNoPointsSelected.tooltip.text"
                ns="skyviewDesign"
              />
            ),
          }}
        >
          {t('section.functions.height.content.infoNoPointsSelected.text', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      ) : (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
          leftTooltip={{
            title: t('section.functions.height.content.infoPointsSelected.tooltip.title', {
              ns: 'skyviewDesign',
            }),
            content: (
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="section.functions.height.content.infoPointsSelected.tooltip.text"
                ns="skyviewDesign"
              />
            ),
          }}
        >
          {t('section.functions.height.content.infoPointsSelected.text', {
            ns: 'skyviewDesign',
            count: pointIds.length,
          })}
        </InfoBox>
      )}

      {pointIds.length > 0 && (
        <>
          <LabelledContainer
            text={t('section.functions.height.content.mode.title', { ns: 'skyviewDesign' })}
          >
            <Select
              options={items.current}
              value={snapMode}
              onChange={(e) => setSnapMode(e.target.value as InterpolationSnapMode)}
            />
          </LabelledContainer>

          {['height', 'relativeHeight'].includes(snapMode) && (
            <LabelledContainer
              text={t('section.functions.height.content.mode.heightInputTitle', {
                ns: 'skyviewDesign',
              })}
            >
              <TextBox
                autoFocus={true}
                hideStepper={true}
                type={'number'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </LabelledContainer>
          )}
        </>
      )}

      <Button
        color="primary"
        disabled={pointIds.length === 0}
        ref={confirmButton}
        variant="contained"
        onClick={async () => {
          if (['pointCloud', 'assets'].includes(snapMode)) {
            progress.setShowDialog(true);
          } else {
            await onSetPointHeights();
          }
        }}
      >
        {t('section.functions.height.content.updateButton', { ns: 'skyviewDesign' })}
      </Button>

      {progress.showDialog && (
        <ProgressDialog
          abortController={progress.abortController.current}
          callback={onSetPointHeights}
          progressController={progress.controller.current}
          title={t('section.functions.height.content.save.progressBar', { ns: 'skyviewDesign' })}
          onClose={() => {
            progress.setShowDialog(false);
          }}
        />
      )}
    </Stack>
  );
};

export { SetPointHeights };
